/**
 * JTALK shared icons: 24px grid, rounded strokes and balanced inner spacing.
 */
import React from 'react';

export const FONT_APPLE_UI =
  '"SF Pro Text", "SF Pro Display", "Hiragino Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';

const common = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function Svg({ size, children, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconChevronLeft({ size = 22 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M15 18l-6-6 6-6" />
    </Svg>
  );
}

export function IconMic({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <rect {...common} x="8.75" y="3" width="6.5" height="11.5" rx="3.25" />
    <path {...common} d="M5.5 11.5v.5a6.5 6.5 0 0 0 13 0v-.5M12 18.5V21M9 21h6" />
  </Svg>;
}

export function IconMicMuted({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <rect {...common} x="8.75" y="3" width="6.5" height="11.5" rx="3.25" />
    <path {...common} d="M5.5 11.5v.5a6.5 6.5 0 0 0 13 0v-.5M12 18.5V21M9 21h6M3 3l18 18" />
  </Svg>;
}

export function IconHeadphones({ size = 18 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M3 14v3a2 2 0 0 0 2 2h1v-7H5a2 2 0 0 0-2 2zM21 14v3a2 2 0 0 1-2 2h-1v-7h1a2 2 0 0 1 2 2z" />
      <path {...common} d="M3 14a9 9 0 0 1 18 0" />
    </Svg>
  );
}

export function IconHandRaised({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M12 3v8M8 15l-2 5M12 15v5M16 15l2 5" />
      <path {...common} d="M8 15a4 4 0 0 1 8 0" />
    </Svg>
  );
}

export function IconWaveform({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M4 12v-2M8 17V7M12 20V4M16 17V7M20 12v-2" />
    </Svg>
  );
}

export function IconBubble({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path
        {...common}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.77 9.77 0 0 1-4-.8L3 21l1.8-3.6A7 7 0 0 1 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8Z"
      />
    </Svg>
  );
}

export function IconGlobe({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <circle {...common} cx="12" cy="12" r="9" />
      <path {...common} d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </Svg>
  );
}

export function IconMoon({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </Svg>
  );
}

export function IconBook({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path {...common} d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </Svg>
  );
}

export function IconCup({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M18 8h1a4 4 0 0 1 0 8h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8Z" />
      <path {...common} d="M6 1v3M10 1v3M14 1v3" />
    </Svg>
  );
}

export function IconSparkle({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M12 3v3M12 18v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M3 12h3M18 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" />
      <circle {...common} cx="12" cy="12" r="2" />
    </Svg>
  );
}

export function IconHeart({ size = 20, filled = false }) {
  return (
    <Svg size={size} aria-hidden>
      {filled
        ? <path fill="currentColor" stroke="none" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        : <path {...common} d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      }
    </Svg>
  );
}

export function IconPencil({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <path {...common} d="m14.5 5 4 4M4 20l4.5-1 11-11a2.83 2.83 0 0 0-4-4l-11 11L4 20Z" />
  </Svg>;
}

export function IconVolume({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <polygon {...common} points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path {...common} d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
    </Svg>
  );
}

export function IconCompose({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <path {...common} d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
    <path {...common} d="m15.5 7.25 3.5 3.5M11 16l3.5-.75 6-6a2.475 2.475 0 0 0-3.5-3.5l-6 6L11 16Z" />
  </Svg>;
}

export function IconPaperclip({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </Svg>
  );
}

export function IconSend({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <line {...common} x1="22" y1="2" x2="11" y2="13" />
      <polygon {...common} points="22 2 15 22 11 13 2 9 22 2" />
    </Svg>
  );
}

export function IconClose({ size = 18 }) {
  return (
    <Svg size={size} aria-hidden>
      <line {...common} x1="18" y1="6" x2="6" y2="18" />
      <line {...common} x1="6" y1="6" x2="18" y2="18" />
    </Svg>
  );
}

export function IconSearch({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <circle {...common} cx="10.5" cy="10.5" r="6.75" />
    <path {...common} d="m15.5 15.5 4.75 4.75" />
  </Svg>;
}

export function IconChevronRight({ size = 18 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M9 18l6-6-6-6" />
    </Svg>
  );
}

export function IconArrowUpRight({ size = 16 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M7 17L17 7M7 7h10v10" strokeWidth="1.75" />
    </Svg>
  );
}

export function IconUserPlus({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <circle {...common} cx="8.5" cy="7" r="3.25" />
    <path {...common} d="M2.75 20v-1.75a5.75 5.75 0 0 1 11.5 0V20M18.5 9.5v7M15 13h7" />
  </Svg>;
}

export function IconUserCheck({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <circle {...common} cx="8.5" cy="7" r="3.25" />
    <path {...common} d="M2.75 20v-1.75a5.75 5.75 0 0 1 11.5 0V20m1-7 2.5 2.5 4.5-5" />
  </Svg>;
}

export function IconMessageCircle({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <path {...common} d="M8 4h8a5 5 0 0 1 5 5v4a5 5 0 0 1-5 5H9l-5 3v-5.25A4.98 4.98 0 0 1 3 13V9a5 5 0 0 1 5-5Z" />
  </Svg>;
}

/** 退室・切断のイメージ */
export function IconLeaveRoom({ size = 20 }) {
  return (
    <Svg size={size} aria-hidden>
      <path {...common} d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </Svg>
  );
}

/** ルーム一覧用：glyph 名 → アイコン */
export function RoomListGlyph({ name, size = 22 }) {
  switch (name) {
    case 'lobby':
      return <IconWaveform size={size} />;
    case 'ja':
      return <IconBubble size={size} />;
    case 'en':
      return <IconGlobe size={size} />;
    case 'night':
      return <IconMoon size={size} />;
    case 'demo-cafe':
      return <IconCup size={size} />;
    case 'demo-class':
      return <IconBook size={size} />;
    case 'demo-party':
      return <IconSparkle size={size} />;
    default:
      return <IconWaveform size={size} />;
  }
}

export function IconHome({ size = 24 }) {
  return <Svg size={size} aria-hidden>
    <path {...common} d="m3 10 7.75-6.2a2 2 0 0 1 2.5 0L21 10M5 8.5V19a1.5 1.5 0 0 0 1.5 1.5H10v-6h4v6h3.5A1.5 1.5 0 0 0 19 19V8.5" />
  </Svg>;
}

export function IconUser({ size = 24 }) {
  return <Svg size={size} aria-hidden>
    <circle {...common} cx="12" cy="7.25" r="3.75" />
    <path {...common} d="M5 20.5v-1a7 5.5 0 0 1 14 0v1H5Z" />
  </Svg>;
}

export function IconSettings({ size = 22 }) {
  return <Svg size={size} aria-hidden>
    <path {...common} d="m9.5 3-.5 2.5-2 1.2-2.4-.8-2.5 4.2L4 12l-1.9 1.9 2.5 4.2 2.4-.8 2 1.2.5 2.5h5l.5-2.5 2-1.2 2.4.8 2.5-4.2L20 12l1.9-1.9-2.5-4.2-2.4.8-2-1.2-.5-2.5Z" />
    <circle {...common} cx="12" cy="12" r="3" />
  </Svg>;
}

export function IconEye({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <path {...common} d="M2.75 12S6 6 12 6s9.25 6 9.25 6S18 18 12 18 2.75 12 2.75 12Z" />
    <circle {...common} cx="12" cy="12" r="2.75" />
  </Svg>;
}

export function IconImage({ size = 20 }) {
  return <Svg size={size} aria-hidden>
    <rect {...common} x="3.5" y="3.5" width="17" height="17" rx="2.5" />
    <circle {...common} cx="8.5" cy="8.5" r="1.5" />
    <path {...common} d="m4 17 5-5 3.5 3.5 3-3 5 5" />
  </Svg>;
}

export function IconPhone({ size = 20 }) {
  return <Svg size={size} aria-hidden><path {...common} d="m7 3 3 5-2 2c1.5 3 3 4.5 6 6l2-2 5 3-1 4C10 22 2 14 3 4l4-1Z" /></Svg>;
}
export function IconFlag({ size = 20 }) {
  return <Svg size={size} aria-hidden><path {...common} d="M5 21V3c5-3 9 3 14 0v10c-5 3-9-3-14 0" /></Svg>;
}
